import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/UserProvider';
import { NotificationContext } from '../context/NotificationProvider';
import { WebsiteContext } from '../context/WebsiteProvider';
import { VendorContext } from '../context/VendorProvider';
import TicketModal from '../components/modals/TicketModal';
import SuccessNotification from '../components/toast/SuccessNotification';
import ErrorNotification from '../components/toast/ErrorNotification';
import { ReactComponent as LogoutIcon } from '../assets/icons/logout.svg';
import { ReactComponent as SettingsIcon } from '../assets/icons/gear.svg';
import { FiBell } from 'react-icons/fi';
import { FaPaperclip } from 'react-icons/fa6';
import NewFeaturesModal from '../components/modals/NewFeaturesModal';
import TermsOfServiceModal from '../components/modals/TermsOfServiceModal';
import AccessibilityButton from '../components/buttons/AccessibilityButton';
import januaryLogo from '../assets/images/JanuaryNavLogo.png';
import februaryLogo from '../assets/images/FebruaryNavLogo.svg';
import marchLogo from '../assets/images/MarchNavLogo.svg';
import aprilLogo from '../assets/images/AprilNavLogo.svg';
import julyLogo from '../assets/images/JulyNavLogo.svg';
import octoberLogo from '../assets/images/OctoberNavLogo.svg';
import novemberLogo from '../assets/images/NovemberNavLogo.svg';
import decemberLogo from '../assets/images/DecemberNavLogo.svg';
import defaultLogo from '../assets/images/NavLogo.svg';

export default function ApplicationShell(props) {
  const {
    logout,
    getUser,
    submitTicket,
    darkMode,
    getUserPreference,
    userState,
    accessibilityHidden,
  } = useContext(UserContext);
  const { closeModal } = useContext(WebsiteContext);
  const { closeVendorModal } = useContext(VendorContext);
  const { getAllNotifications, notifications, setNotifications } =
    useContext(NotificationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [openTicket, setOpenTicket] = useState(false);
  const [ticketType, setTicketType] = useState('');
  const [ticketContent, setTicketContent] = useState('');
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketFiles, setTicketFiles] = useState([]);
  const [showFeedback, setShowFeedback] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');
  const version = process.env.REACT_APP_VERSION || '1.0.0';
  const buildNumber = process.env.REACT_APP_BUILD_NUMBER;
  const [showNewFeaturesModal, setShowNewFeaturesModal] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [activeLink, setActiveLink] = useState(location.pathname);
  const [logo, setLogo] = useState('');

  const handleCloseTermsModal = () => {
    setShowTerms(false);
  };

  const handleCloseNewFeaturesModal = () => {
    setShowNewFeaturesModal(false);
  };

  const getMonthlyLogo = () => {
    const currentDate = new Date();
    const month = currentDate.getMonth();

    switch (month) {
      case 0: return januaryLogo;
      case 1: return februaryLogo;
      case 2: return marchLogo;
      case 3: return aprilLogo;
      case 6: return julyLogo;
      case 9: return octoberLogo;
      case 10: return novemberLogo;
      case 11: return decemberLogo;
      default: return defaultLogo;
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowFeedback(false);
    }, 10000);
    return () => clearTimeout(timeout);
  }, []);

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  useEffect(() => {
    if (userState.token) {
      getUserPreference();
      getAllNotifications().then((data) => setNotifications(data));
      getUser();
    }
    setLogo(getMonthlyLogo());
  }, [userState.token]);

  const navigation = [
    { name: 'Vendors', href: '/vendors', current: activeLink === '/vendors' },
    { name: 'Websites', href: '/websites', current: activeLink === '/websites' },
  ];

  const handleOpenTicket = async () => {
    await getUser();
    setOpenTicket(true);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'video/mp4',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const validFiles = files.filter((file) => allowedTypes.includes(file.type));
    setTicketFiles(validFiles);
  };

  const handleTicketSubmit = async () => {
    if (
      ticketContent.trim() === '' ||
      !ticketType ||
      ticketSubject.trim() === ''
    ) {
      return;
    }
    try {
      const formData = new FormData();
      formData.append('type', ticketType);
      formData.append('subject', ticketSubject);
      formData.append('content', ticketContent);
      ticketFiles.forEach((file) => {
        formData.append('files', file);
      });

      await submitTicket(formData, ticketType);
      setTicketContent('');
      setTicketSubject('');
      setTicketFiles([]);
      setOpenTicket(false);
      setShowSuccess(true);
      setText('Feedback submitted successfully');
    } catch (error) {
      setShowError(true);
      setText(`${error.message}`);
      setOpenTicket(false);
      console.error('Error submitting ticket:', error);
    }
  };

  return (
    <div className={`flex flex-col min-h-screen ${darkMode ? 'dark' : ''}`}>
      <header
        className={`fixed left-0 top-0 w-full bg-[#1976bb] ${
          darkMode ? 'dark:bg-stone-900' : ''
        } z-20 text-white shadow-md`}
      >
        <nav className="mx-auto px-2 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between py-3 sm:py-4 lg:py-4">
            <div className="flex items-center">
              <Link
                to="/vendors"
                className="ml-12"
                onClick={() => {
                  closeModal();
                  closeVendorModal();
                  setActiveLink('/vendors');
                }}
              >
                <img
                  src={logo}
                  alt="DLM Logo"
                  className="block h-12 select-none sm:hidden sm:h-12 md:hidden lg:block lg:h-12"
                />
              </Link>
              <div className="ml-16 flex space-x-4 sm:ml-6 md:ml-10 lg:ml-16">
                {navigation.map((item) => (
                  <span
                    key={item.name}
                    onClick={() => {
                      setActiveLink(item.href);
                      if (item.name === 'Websites') {
                        closeModal();
                        navigate('/websites?view=table', { replace: true });
                      }
                      if (item.name === 'Vendors') {
                        closeVendorModal();
                        navigate('/vendors?view=table', { replace: true });
                      }
                    }}
                    className={`${
                      item.current
                        ? 'select-none bg-[#66b3ee] hover:opacity-75 dark:bg-indigo-500 dark:hover:bg-indigo-600'
                        : ''
                    } select-none rounded-md px-3 py-2 text-sm font-bold text-[#FFFFFF] sm:text-base lg:text-lg cursor-pointer`}
                    aria-current={item.current ? 'page' : undefined}
                  >
                    {item.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-6 mr-6">
              <Link
                to="/notifications"
                title={'Notifications'}
                className="relative"
                onClick={() => {
                  closeModal();
                  closeVendorModal();
                }}
              >
                <FiBell className="text-3xl text-white" />
                {notifications && notifications.length > 0 && (
                  <span className="absolute bottom-6 right-3 -mt-1 rounded-full bg-red-500 px-1 text-xs font-semibold text-white">
                    {notifications.length > 50 ? '50+' : notifications.length}
                  </span>
                )}
              </Link>
              <Link
                to="/settings"
                title={'Settings'}
                onClick={() => {
                  closeModal();
                  closeVendorModal();
                }}
              >
                <SettingsIcon className="h-7 text-white" />
              </Link>
              <button
                onClick={() => {
                  logout();
                  closeModal();
                  closeVendorModal();
                }}
                title={'Logout'}
                className="focus:outline-none"
              >
                <LogoutIcon className="h-7 text-white" />
              </button>
            </div>
          </div>
        </nav>
      </header>
      {showNewFeaturesModal && (
        <NewFeaturesModal
          isOpen={showNewFeaturesModal}
          onClose={handleCloseNewFeaturesModal}
          title="What's New"
        />
      )}
      {showTerms && (
        <TermsOfServiceModal
          isOpen={showTerms}
          onClose={handleCloseTermsModal}
          title="What's New"
        />
      )}

      <main className="flex-grow bg-white dark:bg-stone-800 pt-10 sm:pt-20 lg:pt-20">
        <div className="flex min-h-full w-full bg-gray-100 dark:bg-stone-700">
          <div className="z-10 min-h-full w-full">{props.children}</div>
        </div>
        {!accessibilityHidden && <AccessibilityButton />}
      </main>

      <div className="relative z-50">
        <footer className="w-full bg-stone-200 text-gray-600 dark:border-t-[1px] dark:border-stone-600 dark:bg-stone-800 dark:text-white">
          <div className="container mx-auto px-4 sm:px-8 lg:px-16">
            <div className="flex flex-col items-center justify-center py-2 lg:flex-row">
              <p className="lg:text-md pb-1 text-center text-sm sm:text-base">
                MetroList® Data License Management ©{' '}
                {new Date().getFullYear()}
                <Link to="/changelog" rel="noopener noreferrer">
                  <span> View Changelog </span> {version}
                  {buildNumber ? '-' + buildNumber : null}
                </Link>
              </p>

              <button
                onClick={handleOpenTicket}
                className="lg:text-md absolute right-0 cursor-pointer px-3 pb-1 text-xs text-gray-600 dark:text-white sm:text-sm"
              >
                {showFeedback && (
                  <span className="feedback-text blink-fade mr-3">
                    Submit Feedback ➜
                  </span>
                )}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="inline-block h-6 w-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44.125 2.104.52 4.136 1.153 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082"
                    className="pb-1 text-gray-500 dark:text-indigo-500"
                  />
                </svg>
              </button>
            </div>
          </div>
        </footer>
      </div>

      {openTicket && (
        <TicketModal
          isOpen={openTicket}
          submit={handleTicketSubmit}
          onClose={() => setOpenTicket(false)}
        >
          <select
            className="my-4 block w-full cursor-pointer rounded-md border border-gray-300 bg-gray-100 py-1.5 pl-2 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value)}
            required
          >
            <option disabled value="">
              Select a type
            </option>
            <option>Bug</option>
            <option>Feature Request</option>
          </select>
          <input
            className="mb-4 h-10 w-72 rounded-lg border p-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
            type="text"
            placeholder="Subject"
            value={ticketSubject}
            onChange={(e) => setTicketSubject(e.target.value)}
            maxLength={100}
            required
          />
          <div className="relative">
            <textarea
              name="ticket-comment"
              placeholder="Provide feedback to developers here."
              className="h-44 w-72 rounded-lg border p-2 text-gray-700 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
              value={ticketContent}
              onChange={(e) => setTicketContent(e.target.value)}
              maxLength={1000}
              required
            />
            <div className="absolute bottom-2 right-2 text-sm text-gray-400 dark:text-gray-300">
              {ticketContent.length}/{1000}
            </div>
          </div>
          <div className="mt-4 relative">
            <input
              id="ticket-file-upload"
              type="file"
              multiple
              accept="image/jpeg,image/png,video/mp4,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 dark:text-gray-300"
            />
            <label
              htmlFor="ticket-file-upload"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
            >
              <FaPaperclip className="text-lg" />
            </label>
          </div>

          <style>{`
            input[type="file"]::-webkit-file-upload-button {
              display: none;
            }
            input[type="file"]::file-selector-button {
              display: none;
            }
          `}</style>
        </TicketModal>
      )}

      {showSuccess && (
        <SuccessNotification
          show={showSuccess}
          onClose={closeNotification}
          text={text}
        />
      )}
      {showError && (
        <ErrorNotification
          show={showError}
          onClose={closeNotification}
          text={text}
        />
      )}
    </div>
  );
}